import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { ColDef } from '@ag-grid-community/core'
import { Button, CheckBoxGroup } from 'components/common'
import { columnSchema } from 'utils/schema/tableSchema'
import { useQuery } from 'hooks'
import { CheckboxOptions } from 'types/input'
import { updateTableCols } from 'utils/api/userManagement'
import { ActiveColumnKey, ColumnForm } from 'types/dataTable'

type Props = {
  columns: ColDef[]
  setShowColumnMenu: React.Dispatch<React.SetStateAction<boolean>>
  activeColumnKey: ActiveColumnKey
  defaultSelectedColumns?: string[]
}

const ColumnManager = ({
  columns,
  setShowColumnMenu,
  activeColumnKey,
  defaultSelectedColumns,
}: Props) => {
  const [selectedColumnCount, setSelectedColumnCount] = useState<number>(
    columns.length,
  )
  const [loading, setLoading] = useState<boolean>(false)

  const { setQuery, removeQuery } = useQuery()

  const {
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<ColumnForm>({ resolver: yupResolver(columnSchema) })

  // disable scroll of body when modal is open
  useEffect(() => {
    document.getElementsByTagName('main')[0].style.overflow = 'hidden'

    return () => {
      document.getElementsByTagName('main')[0].style.overflow = 'auto'
    }
  }, [])

  /**
   * To submit visible columns
   */
  const handleVisibleColumn = async (data: ColumnForm) => {
    setLoading(true)
    try {
      if (data.columns?.length === 0) {
        await updateTableCols({ [activeColumnKey]: [] })
        removeQuery('cols')
      } else {
        await updateTableCols({ [activeColumnKey]: data.columns })
        setQuery('cols', data.columns)
      }
    } catch (error) {
      /* empty */
    } finally {
      setLoading(false)
      setShowColumnMenu(false)
    }
  }

  /**
   * To handle default selected columns
   *
   * Creates an array of column id from params
   */
  const tableColumns = useMemo(() => {
    if (!defaultSelectedColumns || defaultSelectedColumns.length === 0) {
      return columns.map((col) => col.field as string)
    }

    return defaultSelectedColumns
  }, [columns, defaultSelectedColumns])

  const options = useMemo(() => {
    return columns.map(
      (col) =>
        ({
          label: col.headerName,
          value: col.field,
          disabled: false,
        }) as CheckboxOptions,
    )
  }, [columns])

  return (
    <div className="absolute z-10 mx-auto w-max translate-y-2  rounded-md bg-white p-7 shadow-md sm:right-[20%]">
      {/* Selected Count */}
      <div className="mb-5 border-b-2 border-gray-200 pb-3">
        <span className="text-sm ">{`${selectedColumnCount} out of ${columns?.length} visible`}</span>
      </div>

      <form onSubmit={handleSubmit(handleVisibleColumn)}>
        <div className={`${errors?.columns?.message ? 'pb-4' : 'pb-1'}`}>
          <CheckBoxGroup
            error={errors?.columns?.message}
            setValue={setValue}
            name="columns"
            options={options}
            setTotalSelected={setSelectedColumnCount}
            defaultSelected={tableColumns}
            displayColumn={3}
          />
        </div>

        <div className="mt-4 flex h-10 items-center gap-2">
          <Button
            buttonText="Save"
            className="w-full max-w-36"
            loading={loading}
          />
          <Button
            className="w-full max-w-36"
            buttonText="Cancel"
            type="submit"
            variant="outline"
            onClick={() => setShowColumnMenu(false)}
            disabled={loading}
          />
        </div>
      </form>
    </div>
  )
}

export default ColumnManager
